import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const WHATSAPP_DR = 'https://wa.me/18495388899?text=Hola%20InnoFlex!%20Me%20gustar%C3%ADa%20una%20demo%20gratis.';

export default function WhatsAppFloat() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY ?? window.pageYOffset ?? 0;
      const nearBottom =
        window.innerHeight + scrollY >= document.body.scrollHeight - 220;

      if (scrollY > 300 && !nearBottom) {
        setVisible(true);
      } else {
        setVisible(false);
        setExpanded(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-expand popup once after 1.8s (only if not dismissed before)
  useEffect(() => {
    if (visible && !popupDismissed) {
      const timer = setTimeout(() => setExpanded(true), 1800);
      return () => clearTimeout(timer);
    }
  }, [visible, popupDismissed]);

  // Auto-collapse popup after 5s
  useEffect(() => {
    if (expanded) {
      const timer = setTimeout(() => setExpanded(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [expanded]);

  // Close X only hides the popup card — the button always stays
  const handleDismissPopup = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded(false);
    setPopupDismissed(true);
  };

  return (
    // Outer wrapper: only depends on visible — NEVER hides the button
    <div
      className={`fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'
      }`}
      data-always-dark
    >
      {/* Popup card — independent visibility */}
      <div
        className="transition-none"
        style={{
          opacity: expanded ? 1 : 0,
          transform: expanded ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.88)',
          pointerEvents: expanded ? 'auto' : 'none',
          transformOrigin: 'bottom right',
          transition: expanded
            ? 'opacity 0.32s ease, transform 0.42s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
            : 'opacity 0.22s ease, transform 0.22s cubic-bezier(0.4, 0, 1, 1)',
        }}
      >
        <div
          className="bg-[#1a1a2e] border border-[#25D366]/30 rounded-2xl p-4 shadow-2xl shadow-black/40 relative"
          style={{ width: 'min(260px, calc(100vw - 3.5rem))' }}
        >
          {/* Dismiss X — only closes the popup, never the button */}
          <button
            onClick={handleDismissPopup}
            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors cursor-pointer touch-manipulation rounded-full hover:bg-white/10"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          <div className="flex items-center gap-3 mb-3 pr-8">
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-[#25D366]/20 border border-[#25D366]/40 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-[#25D366] text-xl"></i>
              </div>
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-[#25D366] border-2 border-[#1a1a2e]"></div>
            </div>
            <div>
              <div className="font-heading text-sm font-bold text-white leading-none">InnoFlex</div>
              <div className="font-body text-[10px] text-[#25D366] mt-0.5">{t('whatsapp_online')}</div>
            </div>
          </div>

          <div className="bg-[#25D366]/10 border border-[#25D366]/15 rounded-xl rounded-tl-none px-3 py-2.5 mb-3">
            <p className="font-body text-xs text-white/80 leading-relaxed">
              {t('whatsapp_msg')}
            </p>
          </div>

          <a
            href={WHATSAPP_DR}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1db954] text-white font-bold text-xs py-2.5 rounded-xl transition-all duration-200 cursor-pointer font-body w-full whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-whatsapp-line text-sm"></i>
            </div>
            {t('whatsapp_chat')}
          </a>
        </div>
      </div>

      {/* Main floating button — always visible when scrolled */}
      <div className="p-3 -m-3">
        <button
          onClick={() => setExpanded((p) => !p)}
          className="relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1db954] shadow-xl shadow-[#25D366]/30 flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-110 active:scale-95 touch-manipulation"
          aria-label="Contactar por WhatsApp"
        >
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25"></span>
          <div className="w-7 h-7 flex items-center justify-center relative z-10">
            <i className={`text-white text-2xl transition-all duration-300 ${expanded ? 'ri-close-line' : 'ri-whatsapp-line'}`}></i>
          </div>
          {!expanded && (
            <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-[#020817] flex items-center justify-center z-20">
              <span className="font-body text-[8px] font-bold text-white leading-none">1</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
